import appInfo from "@common/appInfo";
import { type GridColumnDataType } from "@common/controls/Grid/types";
import { type TimeUnit } from "@common/declarations";
import { type I18n } from "@common/lib/i18n";
import { Users } from "@common/modules/user/types";
import { isNull, isNumber, isUndefined, last, round } from "lodash";
import date from "./date";
import { i18nFormat } from "./i18n";
import { floorDecimals } from "./math";
import { joinDefined } from "./misc";

export const formatAddress = (x: Users.Api.UserAddressBase): string =>
  joinDefined(", ", joinDefined(" ", x.unit, x.street), x.city, joinDefined(" ", x.countryCode, x.postalCode));

export const formatHtml = (data: string | undefined | null) =>
  data &&
  data
    .replace(/<div>/g, "\n")
    .replace(/(<([^>]+)>)/gi, "")
    .replace(/&nbsp;/g, " ");

export const formatToHtml = (message: string): string =>
  message
    // all possible cases of a new line
    .replaceAll("\r\n", "<br/>")
    .replaceAll("\n", "<br/>")
    .replaceAll("\r", "<br/>");

export const formatNumber = (
  value: string | number | undefined | null,
  fractionalDigits?: number,
  significantDigits?: number
): string => {
  if (isNull(value) || isUndefined(value) || value === "") return "";
  if (isUndefined(fractionalDigits)) return value.toString();

  const format = (v, d) => floorDecimals(v, d).toFixed(d);

  if (fractionalDigits) {
    if (!isUndefined(significantDigits)) {
      for (let i = fractionalDigits; i >= significantDigits; i--) {
        if (+last((+value).toFixed(i))! > 0) {
          return format(value, i);
        }
      }
      return format(value, significantDigits);
    }
    return format(value, fractionalDigits);
  }

  return round(+value).toString();
};

type FormatMoneyOptions = {
  noValue?: string;
  digits?: number;
  obfuscate?: boolean;
  trimZero?: boolean;
  quantity?: number;
};
const formatMoneyInner = (
  amount: number | undefined | null,
  isDisplay: boolean,
  currencyCode?: string,
  opts?: FormatMoneyOptions
) =>
  isNull(amount) || isUndefined(amount) || isNaN(amount)
    ? opts?.noValue ?? ""
    : `${
        opts?.obfuscate
          ? "***"
          : (isDisplay ? formatNumberDisplay : formatNumber)(
              amount * (opts?.quantity ?? 1),
              opts?.digits ?? 2,
              2,
              opts?.trimZero
            )
      }${currencyCode ? ` ${currencyCode}` : ""}`;

export const formatMoneyDisplay = (value: any, currencyCode?: string, opts?: FormatMoneyOptions) =>
  formatMoneyInner(+value, true, currencyCode, opts);

export const formatPercentDisplay = (value: any, fractionalDigits?: number) =>
  isNaN(value) ? value : `${(+(value * 100).toFixed(fractionalDigits ?? 2)).toString()}%`;

export const formatNumberDisplay = (
  value: string | number | undefined | null,
  fractionalDigits?: number,
  significantDigits?: number, // TODO,
  trimZero?: boolean
): string => {
  if (isNull(value) || isUndefined(value) || value === "") return "";

  return floorDecimals(+value, fractionalDigits ?? 0).toLocaleString(appInfo.cultureCode, {
    minimumFractionDigits: trimZero ? undefined : fractionalDigits ?? 0,
    maximumFractionDigits: fractionalDigits ?? 0
  });
};

export type FormatDataType = GridColumnDataType;

export const formatDisplayValue = (value: any, dataType: FormatDataType | undefined): any => {
  switch (dataType) {
    case "money": {
      return formatMoneyDisplay(value, appInfo.currencyCode);
    }
    case "moneyBilling": {
      return formatMoneyDisplay(value, appInfo.billingCurrencyCode);
    }
    case "moneyObfuscated": {
      return formatMoneyDisplay(value, appInfo.currencyCode, {
        obfuscate: true
      });
    }
    case "date": {
      return date.formatDate(value);
    }
    case "dateLocal": {
      return date.formatDateLocal(value);
    }
    case "dateTime": {
      return date.formatDateTime(value);
    }
    case "dateTimeLocal": {
      return date.formatDateTimeLocal(value);
    }
    case "decimal2": {
      return formatNumber(value, 2);
    }
    case "percent": {
      return formatPercentDisplay(value, 2);
    }
    case "day": {
      return date.format(value, date.DATE_DAY_FORMAT);
    }
    case "dayName": {
      return date.format(value, date.DATE_DAY_NAME_FORMAT);
    }
    case "monthDay": {
      return date.format(value, date.DATE_MONTH_DAY_FORMAT);
    }
    case "monthName": {
      return date.format(value, date.DATE_MONTH_NAME_FORMAT);
    }
    case "year": {
      return date.format(value, date.DATE_YEAR_FORMAT);
    }
    case "yearMonth": {
      return date.format(value, date.DATE_YEAR_MONTH_FORMAT);
    }
    case "yearMonthDay": {
      return date.formatDate(value);
    }
    default:
      return value;
  }
};

export const formatAspectRation = (aspectRatio: string | number) =>
  isNumber(aspectRatio) ? aspectRatio : aspectRatio.replace(/\s*:\s*/, "/");

type QuarterDisplayMode = "Quarter" | "3Months";
const pluralize = (i18n: I18n, unit: TimeUnit, quarter?: QuarterDisplayMode) => {
  switch (unit) {
    case "Day":
      return i18n.days;
    case "Hour":
      return i18n.hours;
    case "Minute":
      return i18n.minutes;
    case "Month":
      return i18n.months;
    case "Second":
      return i18n.seconds;
    case "Quarter":
      return quarter === "3Months" ? i18n.quarter3Months : i18n.quarters;
    case "Week":
      return i18n.weeks;
    case "Year":
      return i18n.years;
  }
};

export const localizeUnits = (i18n: I18n, unit: TimeUnit | null, quarter?: QuarterDisplayMode) => {
  switch (unit) {
    case "Day":
      return i18n.day;
    case "Hour":
      return i18n.hour;
    case "Minute":
      return i18n.minute;
    case "Month":
      return i18n.month;
    case "Quarter":
      return quarter === "3Months" ? i18n.quarter3Months : i18n.quarter;
    case "Second":
      return i18n.second;
    case "Week":
      return i18n.week;
    case "Year":
      return i18n.year;
    default:
      return null;
  }
};

export const formatFromRepeatWithAmount = (
  i18n: I18n,
  amount: number,
  currency: string,
  interval: number,
  unit: TimeUnit,
  opts?: FormatRepeatWithAmountOpts
) => i18nFormat(i18n.fromValueInfo, ["VALUE", formatRepeatWithAmount(i18n, amount, currency, interval, unit, opts)]);

export const formatFromRepeatWithPercent = (i18n: I18n, percent: number, interval: number, unit: TimeUnit) =>
  i18nFormat(i18n.fromValueInfo, ["VALUE", formatRepeatWithPercent(i18n, percent, interval, unit)]);

type FormatRepeatWithAmountOpts = {
  digits?: number;
  quarter?: QuarterDisplayMode;
};
export const formatRepeatWithAmount = (
  i18n: I18n,
  amount: number,
  currency: string,
  interval: number,
  unit: TimeUnit,
  opts?: FormatRepeatWithAmountOpts
) =>
  `${opts?.digits ? amount.toFixed(opts.digits) : amount} ${formatRepeatWithType(
    i18n,
    currency,
    interval,
    unit,
    opts
  )}`;

export const formatRepeatWithPercent = (i18n: I18n, percent: number, interval: number, unit: TimeUnit) =>
  formatRepeatWithType(i18n, formatPercentDisplay(percent), interval, unit);

export const formatRepeatWithType = (
  i18n: I18n,
  type: string,
  interval: number,
  unit: TimeUnit,
  opts?: FormatRepeatOpts
) => `${type} / ${formatRepeat(i18n, interval, unit, opts)}`;

export const formatRepeatWithTypeShort = (
  i18n: I18n,
  type: string,
  interval: number,
  unit: TimeUnit,
  opts?: FormatRepeatOpts
) => `${type} / ${formatRepeatShort(i18n, interval, unit, opts)}`;

type FormatRepeatOpts = {
  includeOne?: boolean;
  quarter?: QuarterDisplayMode;
};
export const formatRepeat = (i18n: I18n, interval: number, unit: TimeUnit, opts?: FormatRepeatOpts) =>
  interval > 1 || opts?.includeOne
    ? `${interval} ${(interval > 1
        ? pluralize(i18n, unit, opts?.quarter)
        : localizeUnits(i18n, unit, opts?.quarter)
      )?.toLocaleLowerCase()}`
    : localizeUnits(i18n, unit, opts?.quarter)?.toLocaleLowerCase();

export const formatRepeatShort = (i18n: I18n, interval: number, unit: TimeUnit, opts?: FormatRepeatOpts) => {
  const c = unit === "Quarter" && opts?.quarter === "3Months" ? "3m" : localizeUnits(i18n, unit)?.toLocaleLowerCase();
  return interval > 1 ? `${interval} ${c}` : c;
};
